import { useEmulators, useEmulatorStatuses, useSnapshots, type SnapshotWithRoot } from "../../ctrl/hooks";
import { useMemo } from "react";
import { isBooting, normalizeStatus, toEmulatorRow, type EmulatorViewer } from "./emulatorRow";
import { useRestarts } from "./restarts";

export * from "./emulatorRow";

/**
 * Emulators joined with their deployment status and source snapshot.
 * Polls faster while anything is booting (2 s), otherwise 10 s.
 */
export function useEmulatorRows(session: EmulatorViewer) {
  const restarts = useRestarts();
  const restarting = Object.keys(restarts).length > 0;
  // Poll the live list fast while a restart is in flight to catch the dip.
  const list = useEmulators({ refreshInterval: restarting ? 2_000 : 10_000 });
  const ids = useMemo(() => (list.data ?? []).map((e) => e.id ?? "").filter(Boolean), [list.data]);
  const anyBooting =
    restarting || (list.data ?? []).some((e) => isBooting(normalizeStatus(e.status)));
  const statuses = useEmulatorStatuses(ids, { refreshInterval: anyBooting ? 2_000 : 30_000 });
  const snaps = useSnapshots({ revalidateOnFocus: false });

  const rows = useMemo(
    () =>
      (list.data ?? []).map((e) =>
        toEmulatorRow(e, statuses.data?.[e.id ?? ""], snaps.data as SnapshotWithRoot[], session),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list.data, statuses.data, snaps.data, session, restarts],
  );

  return {
    rows,
    mine: rows.filter((r) => r.mine),
    shared: rows.filter((r) => r.shared),
    running: rows.filter((r) => r.status === "Running"),
    isLoading: list.isLoading,
    error: list.error,
    refresh: () => Promise.all([list.mutate(), statuses.mutate()]),
  };
}
